import { SectionHeader, TextBadge } from '@/components/ui';
import { MaskedText } from '@/components/ui/MaskedText';
import { IconDislike, IconLike } from '@repo/svgs';

interface RentIncreaseSectionProps {
  buildingAddress: string;
  neighborhoodName?: string;
  isBlurRequired: boolean;
  tenancyData: any | null;
}

function toPct(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function formatPct(p: number | null): string {
  if (p === null) return '-';
  const rounded = Math.round(p * 10) / 10;
  const s = rounded % 1 === 0 ? String(Math.trunc(rounded)) : String(rounded);
  return `${s}%`;
}

export function RentIncreaseSection({
  buildingAddress,
  neighborhoodName = '',
  isBlurRequired,
  tenancyData,
}: RentIncreaseSectionProps) {
  const buildingPct = toPct(tenancyData?.buildingAverageRentIncrease);
  const neighborhoodPct = toPct(tenancyData?.neighborhoodAverageRentIncrease);

  const showInsight = !isBlurRequired && buildingPct !== null && neighborhoodPct !== null;
  // Positive = lower rent increase than neighborhood
  const isPositive = showInsight ? buildingPct <= neighborhoodPct : null;
  const insightText = showInsight
    ? `The average annual rent increase at ${buildingAddress} is ${
        isPositive ? 'lower' : 'higher'
      } than other buildings in ${neighborhoodName}.`
    : '';

  return (
    <div className="w-full">
      <SectionHeader
        title={`Rent increase at ${buildingAddress}`}
        subtitleText="Openigloo analysis"
      />

      <div className="md:px-4 px-2">
        {/* Insight badge */}
        {isBlurRequired ? (
          <div className="mt-2 rounded-2xl bg-secondary p-4">
            <MaskedText className="h-4 w-3/4" />
          </div>
        ) : showInsight ? (
          <TextBadge
            className="mt-2"
            variant={isPositive ? 'positive' : 'negative'}
            icon={
              isPositive ? (
                <IconLike className="w-4 h-4 icon-success shrink-0" aria-hidden />
              ) : (
                <IconDislike className="w-4 h-4 icon-alert shrink-0" aria-hidden />
              )
            }
          >
            {insightText}
          </TextBadge>
        ) : null}

        {/* Two-column values */}
        <div className="mt-4 grid grid-cols-2">
          <div className="pr-4">
            <div className="text-caption text-secondary">{buildingAddress}</div>
            <div className="mt-2 text-heading-6 text-primary">
              {isBlurRequired ? <MaskedText className="w-16 h-5 mb-1" /> : formatPct(buildingPct)}
            </div>
            <div className="text-body-2 text-primary">Rent increase</div>
          </div>

          <div className="border-l border-tertiary pl-4">
            <div className="text-caption text-secondary">{neighborhoodName}</div>
            <div className="mt-2 text-heading-6 text-primary">
              {isBlurRequired ? (
                <MaskedText className="w-16 h-5 mb-1" />
              ) : (
                formatPct(neighborhoodPct)
              )}
            </div>
            <div className="text-body-2 text-primary">Rent increase</div>
          </div>
        </div>
      </div>
    </div>
  );
}
